// @figma file=pkCGby54zXzTsccXvHqzO6 node=4007:13806 name="Popover" set="my-taible-shadcn-studio" verified=2026-03-22

import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Popover (root) ───────────────────────────────────────────────────────────

/**
 * Non-modal floating content panel anchored to a trigger element.
 *
 * Built on `@radix-ui/react-popover` — provides full ARIA compliance:
 * - Role: `dialog` on the content panel (WCAG 4.1.2 — `aria-dialog-name`)
 * - Keyboard: `Escape` closes the popover and returns focus to the trigger
 * - Focus management: focus moves into the content on open
 * - Positioning: fully configurable via `side` and `align` props
 *
 * ⚠️ **Accessibility requirement**: `PopoverContent` renders `role="dialog"`.
 * axe-core rule `aria-dialog-name` requires every dialog to have an accessible
 * name. You MUST pass either:
 * - `aria-label="<descriptive name>"` on `<PopoverContent>`, **or**
 * - `aria-labelledby="<id>"` referencing a visible heading inside the content.
 *
 * @example
 * ```tsx
 * <Popover>
 *   <PopoverTrigger asChild>
 *     <Button variant="outline">Open popover</Button>
 *   </PopoverTrigger>
 *   <PopoverContent aria-labelledby="my-popover-title">
 *     <p id="my-popover-title" className="font-medium">Dimensions</p>
 *     <p className="text-sm text-muted-foreground">Set the dimensions for the layer.</p>
 *   </PopoverContent>
 * </Popover>
 * ```
 */

export interface PopoverProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Root> {}

const Popover = PopoverPrimitive.Root;
Popover.displayName = 'Popover';

// ─── PopoverTrigger ───────────────────────────────────────────────────────────

export interface PopoverTriggerProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger> {}

const PopoverTrigger = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Trigger>,
  PopoverTriggerProps
>(({ className, ...props }, ref) => (
  <PopoverPrimitive.Trigger ref={ref} className={cn(className)} {...props} />
));
PopoverTrigger.displayName = 'PopoverTrigger';

// ─── PopoverAnchor ────────────────────────────────────────────────────────────

export interface PopoverAnchorProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Anchor> {}

const PopoverAnchor = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Anchor>,
  PopoverAnchorProps
>(({ className, ...props }, ref) => (
  <PopoverPrimitive.Anchor ref={ref} className={cn(className)} {...props} />
));
PopoverAnchor.displayName = 'PopoverAnchor';

// ─── PopoverContent ───────────────────────────────────────────────────────────

export interface PopoverContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  /** Which portal container to render into. Defaults to document.body. */
  container?: HTMLElement | null;
}

const PopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = 'center', side = 'bottom', sideOffset = 4, container, ...props }, ref) => (
  <PopoverPrimitive.Portal container={container}>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      side={side}
      sideOffset={sideOffset}
      className={cn(
        // Positioning — z-index above most overlays
        'z-50',
        // Layout
        'w-72 rounded-[min(var(--radius),0.75rem)]',
        // Surface — floating overlay surface, matches Figma popover token
        'bg-popover text-popover-foreground border border-border',
        // Spacing — 16px padding matches Figma Card Content padding
        'p-4',
        // Elevation — Figma: Light/box-shadow/default/md
        'shadow-md',
        // Focus ring
        'outline-none',
        // Entry/exit animations driven by Radix data-state + data-side
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2',
        'data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2',
        'data-[side=top]:slide-in-from-bottom-2',
        'duration-200',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = 'PopoverContent';

// ─── PopoverClose ─────────────────────────────────────────────────────────────

export interface PopoverCloseProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Close> {}

const PopoverClose = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Close>,
  PopoverCloseProps
>(({ className, ...props }, ref) => (
  <PopoverPrimitive.Close ref={ref} className={cn(className)} {...props} />
));
PopoverClose.displayName = 'PopoverClose';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverTrigger };
